import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorCode,
  apiErrorText,
  useListContractEstimatesQuery,
  useListContractsQuery,
  useMakeContractEstimateActiveMutation,
  useUploadContractEstimateMutation,
} from '@shared/api';
import type { ContractEstimateUploadResult, ContractEstimateVersion } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Combobox } from '@shared/ui/Combobox';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { ParseWarningsBanner } from '../ParseWarningsBanner';
import {
  CONFIRMATION_REQUIRED,
  estimateDateLabel,
  estimateSourceLabel,
  estimateStatusLabel,
  estimateTotalLabel,
  versionList,
} from '../contractEstimateLabels';
import {
  EstimateUploadAttempts,
  isFreshResponse,
  snapshotMatchesForm,
  uploadFormData,
} from '../estimateUploadAttempt';
import type {
  EstimateUploadForm,
  EstimateUploadSnapshot,
  PendingConfirmation,
} from '../estimateUploadAttempt';
import {
  CheckboxLabel,
  Err,
  FileHint,
  Muted,
  MutedMargin,
  ObjName,
  SubmitWrapper,
  Table,
  TableTitle,
} from './styled';

const BannerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Notice = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Note = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.success};
`;

interface BenchmarkUploadCardProps {
  contractId: string;
  setContractId: (id: string) => void;
  canWrite: boolean;
}

/** Итог загрузки для сметчика: какая версия стала действующей, что ушло в архив. */
function uploadedNote(res: ContractEstimateUploadResult): string {
  const archived = res.superseded ?? [];
  const tail =
    archived.length > 0
      ? ` В архив: ${versionList(archived.map((n) => ({ version_no: n })))}.`
      : '';
  return `Загружена версия v${res.version_no} — теперь она действующая.${tail}`;
}

export function BenchmarkUploadCard({
  contractId,
  setContractId,
  canWrite,
}: BenchmarkUploadCardProps) {
  const [contractLabel, setContractLabel] = useState('');
  const [contractQuery, setContractQuery] = useState('');
  const [estFile, setEstFile] = useState<File | null>(null);
  const [estFormat, setEstFormat] = useState('lsr');
  const [estIsAmendment, setEstIsAmendment] = useState(false);
  const [estComment, setEstComment] = useState('');
  const [estError, setEstError] = useState<string | null>(null);
  const [estWarnings, setEstWarnings] = useState<string[]>([]);
  // Ответ 409 `confirmation_required`: разбор выглядит неправдоподобно, версия
  // не создана. Сметчик видит причину и может загрузить ИМЕННО проверенный
  // файл явно — предупреждение хранит снимок попытки, а не текст.
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [versionError, setVersionError] = useState<string | null>(null);
  // Номера попыток и текущая форма — чтобы поздний ответ на старую попытку
  // не лёг на новый файл или договор (Z2, аудит 35).
  const attemptsRef = useRef<EstimateUploadAttempts | null>(null);
  if (attemptsRef.current === null) attemptsRef.current = new EstimateUploadAttempts();
  const attempts = attemptsRef.current;
  const form: EstimateUploadForm = {
    contractId,
    file: estFile,
    format: estFormat,
    isAmendment: estIsAmendment,
    comment: estComment,
  };
  const formRef = useRef(form);
  formRef.current = form;

  const { setActiveProject } = useActiveProjectContext();

  const estimatesQ = useListContractEstimatesQuery(
    { contractId: contractId.trim() },
    { skip: contractId.trim().length < 8 },
  );
  // Серверный поиск договоров (по номеру / контрагенту / предмету) — список
  // растёт, клиентский фильтр не масштабируется. Пустой запрос → свежие 20.
  const contractsQ = useListContractsQuery({
    search: contractQuery.trim() || undefined,
    limit: 20,
  });
  const [uploadEstimate, { isLoading: uploadingEst }] = useUploadContractEstimateMutation();
  const [makeActive, { isLoading: activating }] = useMakeContractEstimateActiveMutation();

  const estimates: ContractEstimateVersion[] = estimatesQ.data?.items ?? [];
  const activeVersions = estimates.filter((es) => es.status === 'active');
  const multiActive = activeVersions.length > 1;
  // Сверки берут новейшую действующую версию — её и подсвечиваем.
  const currentId = activeVersions.reduce<ContractEstimateVersion | null>(
    (best, es) => (best === null || es.version_no > best.version_no ? es : best),
    null,
  )?.id;
  const contracts = contractsQ.data?.items ?? [];
  const contractOptions = contracts.map((c) => ({
    id: c.id,
    label: c.contract_number ? `№ ${c.contract_number} · ${c.counterparty}` : c.counterparty,
    sublabel: c.subject ?? undefined,
  }));

  useEffect(() => {
    if (!contractId) return;
    const c = contractsQ.data?.items.find((x) => x.id === contractId);
    if (c?.project_id) {
      setActiveProject(c.project_id);
    }
  }, [contractId, contractsQ.data?.items, setActiveProject]);

  // Сменили договор, файл или формат — отправленные попытки устарели, а
  // предупреждение больше не про то, что сейчас в форме.
  useEffect(() => {
    attempts.invalidate();
    setPending(null);
  }, [attempts, contractId, estFile, estFormat]);

  async function send(snapshot: EstimateUploadSnapshot, confirm: boolean) {
    setEstError(null);
    setEstWarnings([]);
    setNote(null);
    setPending(null);
    // Ответ применяется, только если это последняя попытка и форма не
    // менялась: поздний ответ на прежний файл чужую попытку не трогает.
    const fresh = () => isFreshResponse(attempts, snapshot, formRef.current);
    try {
      const res = await uploadEstimate(uploadFormData(snapshot, confirm)).unwrap();
      if (!fresh()) return;
      setEstWarnings(res.warnings ?? []);
      setNote(uploadedNote(res));
      setEstFile(null);
      setEstIsAmendment(false);
      setEstComment('');
    } catch (err) {
      if (!fresh()) return;
      if (apiErrorCode(err) === CONFIRMATION_REQUIRED) {
        setPending({
          snapshot,
          message: apiErrorText(
            err,
            'Разбор файла выглядит неправдоподобно — проверьте файл и формат.',
          ),
        });
        return;
      }
      setEstError(apiErrorText(err, 'Не удалось загрузить договорную смету.'));
      // eslint-disable-next-line no-console
      console.error('uploadContractEstimate failed', err);
    }
  }

  async function handleUploadEstimate(e: React.FormEvent) {
    e.preventDefault();
    if (!contractId.trim()) {
      setEstError('Выберите договор из списка.');
      return;
    }
    if (!estFile) {
      setEstError('Приложите файл договорной сметы (xlsx).');
      return;
    }
    const snapshot = attempts.start(form);
    if (snapshot) await send(snapshot, false);
  }

  /** «Всё равно загрузить»: отправить ровно тот снимок, о котором предупредил
   *  сервер, и только пока форма показывает его же. */
  async function handleConfirm() {
    const confirmed = pending;
    const current = formRef.current;
    if (!confirmed || !snapshotMatchesForm(confirmed.snapshot, current)) {
      setPending(null);
      return;
    }
    // Договор, файл и формат — из снимка; отметку ДС и комментарий (подпись
    // версии, сервер их не проверял) — какими сметчик видит их сейчас.
    const snapshot = attempts.restart({
      ...confirmed.snapshot,
      isAmendment: current.isAmendment,
      comment: current.comment.trim(),
    });
    await send(snapshot, true);
  }

  async function handleMakeActive(es: ContractEstimateVersion) {
    const others = estimates.filter((x) => x.id !== es.id && x.status === 'active');
    const question =
      others.length > 0
        ? `Сделать v${es.version_no} единственной действующей версией? ${versionList(others)} уйдут в архив, сверки пойдут по v${es.version_no}.`
        : `Сделать v${es.version_no} действующей версией? Сверки пойдут по ней.`;
    if (!window.confirm(question)) return;
    setVersionError(null);
    setNote(null);
    try {
      const res = await makeActive(es.id).unwrap();
      const archived =
        res.superseded.length > 0
          ? ` В архив: ${versionList(res.superseded.map((n) => ({ version_no: n })))}.`
          : '';
      setNote(`v${res.version_no} — теперь единственная действующая версия.${archived}`);
    } catch (err) {
      setVersionError(apiErrorText(err, 'Не удалось сделать версию действующей.'));
    }
  }

  return (
    <Card>
      <form onSubmit={handleUploadEstimate}>
        <ObjName>1. Договорная смета (эталон)</ObjName>
        <Field>
          <Label>Договор (из агента Статут)</Label>
          <Combobox
            id="ks-contract"
            value={contractId}
            selectedLabel={contractLabel}
            options={contractOptions}
            onQueryChange={setContractQuery}
            loading={contractsQ.isFetching}
            disabled={uploadingEst}
            placeholder="Начните вводить № или название договора"
            emptyText="Договоры не найдены"
            onChange={(opt) => {
              setContractId(opt?.id ?? '');
              setContractLabel(opt?.label ?? '');
            }}
          />
          <FileHint>
            Поиск по номеру / названию / контрагенту (из агента Статут). Нет договора? Сначала
            загрузите его в Статуте.
          </FileHint>
        </Field>
        <Field>
          <Label>Файл сметы</Label>
          <Input
            type="file"
            accept={estFormat === 'boq' ? '.xlsx,.xlsm,.pdf' : '.xlsx,.xlsm'}
            disabled={uploadingEst}
            onChange={(e) => setEstFile(e.target.files?.[0] ?? null)}
          />
          <FileHint>
            ЛСР «17 граф» (Гранд-Смета) — только Excel (xlsx). РДЦ / BOQ (формат A) — Excel
            или PDF.
          </FileHint>
        </Field>
        <Field>
          <Label>Формат</Label>
          <Select
            value={estFormat}
            disabled={uploadingEst}
            onChange={(e) => setEstFormat(e.target.value)}
          >
            <option value="lsr">ЛСР (Гранд-Смета)</option>
            <option value="boq">РДЦ / BOQ (формат A)</option>
          </Select>
        </Field>
        <Field>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={estIsAmendment}
              disabled={uploadingEst}
              onChange={(e) => setEstIsAmendment(e.target.checked)}
            />
            Версия по доп. соглашению (ДС)
          </CheckboxLabel>
          <FileHint>
            Отметка попадёт в комментарий версии. Новая версия в любом случае становится
            действующей, прежняя уходит в архив.
          </FileHint>
        </Field>
        <Field>
          <Label>Комментарий (необязательно)</Label>
          <Input
            value={estComment}
            disabled={uploadingEst}
            onChange={(e) => setEstComment(e.target.value)}
            placeholder="напр. РДЦ по подписанному ДС №… / по подписанным ЗВИ"
          />
          <FileHint>Помогает отличать версии (ДС) и документы в списке выбора.</FileHint>
        </Field>
        {estError && <Err>{estError}</Err>}
        {pending && (
          <FallbackBanner title="Проверьте файл перед загрузкой">
            {pending.message}
            <BannerActions>
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                disabled={uploadingEst || !canWrite}
                onClick={() => void handleConfirm()}
              >
                Всё равно загрузить
              </Button>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={uploadingEst}
                onClick={() => setPending(null)}
              >
                Отмена
              </Button>
            </BannerActions>
          </FallbackBanner>
        )}
        <ParseWarningsBanner warnings={estWarnings} />
        <SubmitWrapper>
          <Button
            type="submit"
            $variant="primary"
            disabled={uploadingEst || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {uploadingEst ? 'Загрузка…' : 'Загрузить эталон'}
          </Button>
        </SubmitWrapper>
      </form>
      {contractId.trim().length >= 8 && (
        <>
          <TableTitle>
            Версии эталона <Badge $tone="neutral">{estimates.length}</Badge>
          </TableTitle>
          {note && (
            <Notice>
              <Note role="status">{note}</Note>
            </Notice>
          )}
          {versionError && (
            <Notice>
              <Err>{versionError}</Err>
            </Notice>
          )}
          {multiActive && (
            <Notice>
              <FallbackBanner title="Несколько действующих версий">
                {`У договора несколько действующих версий (${versionList(activeVersions)}). Оставьте одну: нажмите «Сделать единственной действующей» у верной версии — остальные уйдут в архив. Пока сверки идут по новейшей.`}
              </FallbackBanner>
            </Notice>
          )}
          {estimates.length === 0 ? (
            <MutedMargin>Для этого договора эталон ещё не загружен.</MutedMargin>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Версия</th>
                  <th>Формат</th>
                  <th>Статус</th>
                  <th>Позиций</th>
                  <th>Сумма</th>
                  <th>Загружена</th>
                  <th>Комментарий</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {estimates.map((es) => {
                  const isOnlyActive = es.status === 'active' && !multiActive;
                  return (
                    <tr key={es.id}>
                      <td>v{es.version_no}</td>
                      <td>{estimateSourceLabel(es.source)}</td>
                      <td>
                        <Badge $tone={es.status === 'active' ? 'success' : 'neutral'}>
                          {estimateStatusLabel(es.status)}
                        </Badge>
                        {multiActive && es.id === currentId && (
                          <Muted>по ней идут сверки</Muted>
                        )}
                      </td>
                      <td className="num">{es.item_count}</td>
                      <td className="num">{estimateTotalLabel(es.base_total)}</td>
                      <td>
                        <Muted>{estimateDateLabel(es.created_at)}</Muted>
                      </td>
                      <td>
                        <Muted>{es.comment ?? '—'}</Muted>
                      </td>
                      <td>
                        {canWrite && !isOnlyActive && (
                          <Button
                            type="button"
                            $variant="ghost"
                            $size="sm"
                            disabled={activating}
                            onClick={() => void handleMakeActive(es)}
                          >
                            Сделать единственной действующей
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
}
